"use client";

import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
  Card,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaPlus,
  FaTrash,
  FaCog,
  FaChevronUp,
  FaChevronDown,
  FaEdit,
  FaUsers,
} from "react-icons/fa";
import { motion, AnimatePresence, Variants } from "framer-motion";
import axios from "axios";
import Swal from "sweetalert2";
import PromoCodeModal from "./CodePromoModal";
import DiscountLimitsManagement from "./DiscountLimitsManagement";
import "../../../css/gestionCodesPromo.css";
import EditPromoCodeModal from "./EditPromoCodeModal";

interface PromoCode {
  id: number;
  code: string;
  description: string;
  discountPercentage: number;
  isActive: boolean;
  usedBy: string[];
  createdAt: string;
}

const GestionCodesPromo: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [showDiscountLimits, setShowDiscountLimits] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedPromoCode, setSelectedPromoCode] =
    useState<PromoCode | null>(null);

  // Animation variants
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const handleEditPromoCode = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode);
    setShowEditModal(true);
  };

  useEffect(() => {
    fetchPromoCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPromoCodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token d'authentification manquant");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/promo-code`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        }
      );

      if (response.data && Array.isArray(response.data.promoCodes)) {
        setPromoCodes(response.data.promoCodes);
      } else if (response.data && Array.isArray(response.data)) {
        setPromoCodes(response.data);
      } else {
        throw new Error("Format de réponse invalide");
      }
    } catch (err: any) {
      console.error(err);
      if (err.response?.status === 401)
        setError("Session expirée. Veuillez vous reconnecter.");
      else if (err.response?.status === 403)
        setError("Vous n'avez pas l'autorisation d'accéder à cette ressource.");
      else if (err.code === "ECONNABORTED")
        setError("Timeout de connexion. Veuillez réessayer.");
      else
        setError(
          err.response?.data?.message ||
          "Impossible de charger les codes promo. Veuillez réessayer plus tard."
        );
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePromoCode = async (id: number) => {
    if (deletingIds.has(id)) return;

    const result = await Swal.fire({
      title: "Êtes-vous sûr ?",
      text: "Cette action est irréversible !",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
    });

    if (!result.isConfirmed) return;

    setDeletingIds((prev) => new Set(prev).add(id));

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token d'authentification manquant");

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/promo-code/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        }
      );

      setPromoCodes((prev) => prev.filter((code) => code.id !== id));

      Swal.fire({
        title: "Supprimé !",
        text: "Code promo supprimé.",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (err: any) {
      console.error(err);
      let errorMessage = "Impossible de supprimer le code promo";
      if (err.response?.status === 404) {
        errorMessage = "Ce code promo n'existe plus";
        setPromoCodes((prev) => prev.filter((code) => code.id !== id));
      } else if (err.response?.status === 401)
        errorMessage = "Session expirée. Veuillez vous reconnecter.";
      else if (err.response?.status === 403)
        errorMessage = "Vous n'avez pas l'autorisation de supprimer ce code promo.";
      else if (err.response?.status === 409)
        errorMessage =
          "Ce code promo ne peut pas être supprimé car il est utilisé.";
      else if (err.code === "ECONNABORTED")
        errorMessage = "Timeout de connexion. Veuillez réessayer.";
      else if (err.response?.data?.message) errorMessage = err.response.data.message;

      Swal.fire({ title: "Erreur", text: errorMessage, icon: "error" });
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const getDiscountColor = (percentage: number): string => {
    if (percentage === 100) return "#2A919D"; 
    if (percentage >= 70) return "#4BB0C0";  
    if (percentage >= 50) return "#eb8635";  
    return "#dc3545";                         
  };
  const filteredCodes = promoCodes.filter(
    (code) =>
      code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (code.description &&
        code.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="gestion-codes-promo-container">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="header-section"
      >
        {/* <div className="title-container">
          <h2 className="page-title">Gestion des Codes Promotionnels</h2>
        </div> */}

        <div className="d-flex align-items-center flex-wrap gap-2">
          <h2 className="gc-user-page-title" style={{ color: "#2a919d", margin: 0 }}>
            <FaUsers className="me-2 gc-user-header-icon" /> Gestion des Codes Promotionnels
          </h2>
          <p className="gc-user-page-subtitle" style={{ color: "#2a919d" }}>
            Gérez les codes promotionnels individuels ou appliquez un code à tous les utilisateurs.
          </p>
        </div>

        <div className="actions-container">
          <Form.Control
            type="text"
            placeholder="Rechercher un code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <Button
            variant="primary"
            className="add-button"
            onClick={() => setShowModal(true)}
          >
            <FaPlus /> Nouveau Code
          </Button>
        </div>
      </motion.div>

      {/* Affichage erreurs */}
      {error && (
        <Alert
          variant="danger"
          className="mt-3"
          dismissible
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Table des codes promo */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner-container">
            <Spinner animation="border" variant="primary" className="custom-spinner" />
            <p className="loading-text">Chargement des codes promotionnels…</p>
          </div>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="table-container"
        >
          <div className="scroll-x">
            <Table striped bordered hover className="promo-codes-table">
              <thead>
                <tr>
                  <th style={{ width: "100px" }}>Code</th>
                  <th className="description-cell">Description</th>
                  <th>Remise</th>
                  <th style={{ width: "90px" }}>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filteredCodes.length > 0 ? (
                    filteredCodes.map((code) => (
                      <motion.tr
                        key={code.id}
                        variants={itemVariants}
                        exit={{ opacity: 0, x: -100 }}
                        layout
                      >
                        <td>{code.code}</td>

                        {/* Description: cellule tronquée avec tooltip (affiche toujours) */}
                        <td className="description-cell">
                          {code.description ? (
                            <OverlayTrigger
                              placement="top"
                              trigger={["hover", "focus", "click"]}
                              delay={{ show: 200, hide: 50 }}
                              overlay={
                                <Tooltip id={`tooltip-desc-${code.id}`}>
                                  <div style={{ whiteSpace: "normal" }}>
                                    {code.description}
                                  </div>
                                </Tooltip>
                              }
                            >
                              <span
                                className="truncated-text"
                                aria-label={code.description}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => e.preventDefault()}
                              >
                                {code.description}
                              </span>
                            </OverlayTrigger>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          <Badge
                            className="promo-badge"
                            style={{
                              ["--promo-badge-bg" as any]: getDiscountColor(code.discountPercentage),
                            }}
                          >
                            {code.discountPercentage}%
                          </Badge>

                        </td>
                        <td>{new Date(code.createdAt).toLocaleDateString("fr-FR")}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant=""
                              size="sm"
                              onClick={() => handleEditPromoCode(code)}
                              disabled={deletingIds.has(code.id)}
                              title="Modifier le pourcentage"
                              style={{ color: "#4BB0C0", borderColor: "#4BB0C0" }}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeletePromoCode(code.id)}
                              disabled={deletingIds.has(code.id)}
                            >
                              {deletingIds.has(code.id) ? (
                                <Spinner as="span" animation="border" size="sm" />
                              ) : (
                                <FaTrash />
                              )}
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center">
                        {searchTerm
                          ? "Aucun code promo trouvé pour cette recherche"
                          : "Aucun code promotionnel n'a été créé"}
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </Table>
          </div>
        </motion.div>
      )}

      {/* Gestion des limites de remise */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-5"
      >
        <Card className="discount-limits-card">
          <Card.Header
            onClick={() => setShowDiscountLimits(!showDiscountLimits)}
            className="discount-limits-header d-flex align-items-center bg-light"
          >
            <h4 className="mb-0 fw-bold d-flex align-items-center" style={{ color: "#2A919D" }}>
              <FaCog className="me-2" /> Gestion des limites pour les codes promotionnels
            </h4>
            <div className="ms-auto discount-limits-toggle-icon">
              {showDiscountLimits ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </Card.Header>
          <AnimatePresence>
            {showDiscountLimits && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card.Body className="p-0">
                  <DiscountLimitsManagement className="border-0" />
                </Card.Body>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      </motion.div>

      <PromoCodeModal show={showModal} onClose={() => setShowModal(false)} onCreated={fetchPromoCodes} />

      <EditPromoCodeModal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPromoCode(null);
        }}
        promoCode={selectedPromoCode}
        onUpdated={fetchPromoCodes}
      />
    </div>
  );
};

export default GestionCodesPromo;
